@if (auditResult) {
  <!-- Multi-Page Audit UI -->
  @if (isMultiPageAudit) {
    <div class="landing-page-audit-container">
      <!-- Base Domain and Summary Side by Side -->
      <div class="domain-summary-wrapper">
        <!-- Base Domain Display -->
        @if (baseDomain) {
          <div class="base-domain-section">
            <div class="url-info-section">
              <div class="url-header">
                <mat-icon>language</mat-icon>
                <h4>Base Domain</h4>
              </div>
              <a [href]="baseDomain" target="_blank" rel="noopener noreferrer" class="url-link">
                {{ baseDomain }}
                <mat-icon>open_in_new</mat-icon>
              </a>
            </div>
          </div>
        }

        <!-- Multi-Page Audit Summary Card -->
        @if (getSummary(); as summary) {
          <div class="summary-card">
            <h3>Multi-Page Audit Summary</h3>
            <div class="summary-stats">
              <div class="stat-item">
                <span class="stat-label">Total Pages</span>
                <span class="stat-value">{{ summary.total_pages || discoveredPages.length }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Completed</span>
                <span class="stat-value">{{ summary.completed || getCompletedPagesCount() }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Average Score</span>
                <span class="stat-value">{{ (summary.average_score || getAverageScore()).toFixed(2) }}/10</span>
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Accordion List of Pages -->
      <div class="accordion-section">
        <h3>Discovered Pages</h3>
        <div class="accordion-list">
          @for (page of discoveredPages; track page.url) {
            @let pageData = processSinglePageData(page.result);
            <div class="accordion-item">
              <div 
                class="accordion-header" 
                (click)="toggleAccordion(page.url)"
                [class.expanded]="isAccordionExpanded(page.url)">
                <div class="accordion-header-left">
                  <button class="accordion-toggle" [class.expanded]="isAccordionExpanded(page.url)">
                    <mat-icon>{{ isAccordionExpanded(page.url) ? 'remove' : 'add' }}</mat-icon>
                  </button>
                  <span class="page-url">{{ page.url }}</span>
                </div>
                <div class="accordion-header-right">
                  @if (page.status === 'completed' && page.compositeScore !== undefined) {
                    <span class="score-badge" [style.color]="getScoreColor(page.compositeScore)">
                      {{ page.compositeScore.toFixed(2) }}/10
                    </span>
                  }
                  @if (page.status === 'auditing') {
                    <mat-spinner diameter="20"></mat-spinner>
                    <span class="status-text">Auditing...</span>
                  }
                  @if (page.status === 'pending') {
                    <mat-icon class="pending-icon" title="Pending audit">schedule</mat-icon>
                    <span class="status-text">Pending</span>
                  }
                  @if (page.status === 'failed') {
                    <mat-icon class="error-icon" title="Audit failed">error</mat-icon>
                    <span class="error-text">Failed</span>
                  }
                </div>
              </div>
              
              @if (isAccordionExpanded(page.url)) {
                <!-- Show pending state if page hasn't been audited yet -->
                @if (page.status === 'pending' || !page.result) {
                  <div class="pending-message-container">
                    <mat-icon class="pending-icon-large">schedule</mat-icon>
                    <div class="pending-message-content">
                      <h4>Page Not Yet Audited</h4>
                      <p>This page has been discovered but hasn't been audited yet. Audit results will appear here once processing is complete.</p>
                    </div>
                  </div>
                }
                
                <!-- Show error message if page failed -->
                @if (page.status === 'failed' && page.result?.error) {
                  <div class="error-message-container">
                    <mat-icon class="error-icon-small">error_outline</mat-icon>
                    <span class="error-message-text">{{ page.result.error }}</span>
                  </div>
                }
                
                @if (page.result && page.status === 'completed') {
                <div class="accordion-content">
                  <!-- Single Page Audit Results -->
                  <div class="page-audit-results">
                    <!-- Composite Score and Area Chart Side by Side -->
                    <div class="score-chart-row">
                      <!-- Composite Score -->
                      <div class="score-widget-section">
                        <app-composite-score-widget 
                          [compositeScore]="pageData.compositeScore" 
                          [title]="'Page Audit Score'" 
                          [size]="'medium'">
                        </app-composite-score-widget>
                      </div>

                      <!-- Area Chart -->
                      @if (pageData.areaChartData && pageData.areaChartData.length > 0) {
                        <div class="chart-section">
                          <div class="chart-header">
                            <div class="chart-title-section">
                              <h4 class="chart-title">Current and Potential Scores</h4>
                              <p class="chart-subtitle">Comparison of current scores vs potential improvement across audit areas</p>
                            </div>
                          </div>
                          <div class="chart-mobile-wrapper">
                            <div class="area-chart-wrapper">
                              <app-area-chart
                                [areaChartData]="pageData.areaChartData"
                                [scheme]="'vivid'"
                                [animations]="true"
                                [legend]="false"
                                [xAxis]="true"
                                [yAxis]="true"
                                [showXAxisLabel]="false"
                                [showYAxisLabel]="false"
                                [xAxisLabel]="''"
                                [yAxisLabel]="''"
                                [overideView]="[500, 500]"
                                [maxValue]="10">
                              </app-area-chart>
                            </div>
                          </div>
                        </div>
                      }
                    </div>

                    <!-- Audit Areas Summary -->
                    @if (getPageCategories(pageData).length > 0) {
                      <div class="results-section">
                        <div class="performance-table">
                          <div class="table-header">
                            <div class="table-title-section">
                              <h3>Audit Areas Summary</h3>
                            </div>
                          </div>
                          <div class="table-container">
                            <table class="audit-table">
                              <thead>
                                <tr>
                                  <th>Audit Area</th>
                                  <th>Current Score</th>
                                  <th>Potential Score</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                @for (category of getPageCategories(pageData); track category) {
                                  @let areaData = pageData.auditAreas[category];
                                  <tr>
                                    <td class="category-cell">{{ category }}</td>
                                    <td>
                                      <span class="score-value" [style.color]="getScoreColor(areaData.current)">
                                        {{ areaData.current }}/10
                                      </span>
                                    </td>
                                    <td>
                                      <span class="score-value" [style.color]="getScoreColor(areaData.potential)">
                                        {{ areaData.potential }}/10
                                      </span>
                                    </td>
                                    <td>
                                      @if (isPass(areaData.current)) {
                                        <span class="status-badge pass">
                                          <mat-icon>check_circle</mat-icon>
                                          Pass
                                        </span>
                                      } @else {
                                        <span class="status-badge fail">
                                          <mat-icon>cancel</mat-icon>
                                          Fail
                                        </span>
                                      }
                                    </td>
                                  </tr>
                                }
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    }

                    <!-- Failed Questions -->
                    @if (getPageFailedQuestions(pageData).length > 0) {
                      <div class="results-section">
                        <div class="performance-table">
                          <div class="table-header">
                            <div class="table-title-section">
                              <h3>Issues and Recommendations</h3>
                            </div>
                          </div>
                          <div class="table-container">
                            <table class="audit-table">
                              <thead>
                                <tr>
                                  <th>Audit Area</th>
                                  <th>Question</th>
                                  <th>Evidence</th>
                                </tr>
                              </thead>
                              <tbody>
                                @for (item of getPageFailedQuestions(pageData); track item.question) {
                                  <tr>
                                    <td>{{ item.category }}</td>
                                    <td>{{ item.question }}</td>
                                    <td class="evidence-cell">{{ item.evidence || 'No evidence provided' }}</td>
                                  </tr>
                                }
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                </div>
                }
              }
            </div>
          }
        </div>
      </div>
    </div>
  } @else {
    <!-- Single-Page Audit UI (Backward Compatibility) -->
    <div class="landing-page-audit-container">
      <!-- URL Info Section -->
      @if (getUrl()) {
        <div class="url-info-section">
          <div class="url-header">
            <mat-icon>language</mat-icon>
            <h4>Audited Website</h4>
          </div>
          <a [href]="getUrl()" target="_blank" rel="noopener noreferrer" class="url-link">
            {{ getUrl() }}
            <mat-icon>open_in_new</mat-icon>
          </a>
        </div>
      }

      <!-- Composite Score and Area Chart Side by Side -->
      <div class="score-chart-row">
        <!-- Composite Score Widget -->
        <div class="score-widget-section">
          <app-composite-score-widget 
            [compositeScore]="compositeScore" 
            [title]="'Page Audit Score'" 
            [size]="'medium'">
          </app-composite-score-widget>
        </div>
    
        <!-- Area Chart Section (Current vs Potential) -->
        @if (areaChartData && areaChartData.length > 0) {
          <div class="chart-section">
            <div class="chart-header">
              <div class="chart-title-section">
                <h4 class="chart-title">Current and Potential Scores</h4>
                <p class="chart-subtitle">Comparison of current scores vs potential improvement across audit areas</p>
                <p class="chart-info-text">
                  <small>Score > 8 = Pass, Score ≤ 8 = Fail</small>
                </p>
              </div>
            </div>
            <div class="chart-mobile-wrapper">
              <div class="area-chart-wrapper">
                <app-area-chart
                  [areaChartData]="areaChartData"
                  [scheme]="'vivid'"
                  [animations]="true"
                  [legend]="false"
                  [xAxis]="true"
                  [yAxis]="true"
                  [showXAxisLabel]="false"
                  [showYAxisLabel]="false"
                  [xAxisLabel]="''"
                  [yAxisLabel]="''"
                  [maxValue]="10">
                </app-area-chart>
              </div>
            </div>
          </div>
        }
      </div>
  
    <!-- Audit Areas Summary Table -->
    @if (getCategories().length > 0) {
      <div class="results-section">
        <div class="performance-table">
          <div class="table-header">
            <div class="table-title-section">
              <h3>Audit Areas Summary</h3>
              <p class="table-subtitle">
                Detailed assessment across all audit areas
                <span class="info-text">
                  <small>(Score > 8 = Pass, Score ≤ 8 = Fail)</small>
                </span>
              </p>
            </div>
          </div>
          <div class="table-container">
            <table class="audit-table">
              <thead>
                <tr>
                  <th>Audit Area</th>
                  <th>Current Score</th>
                  <th>Potential Score</th>
                  <th>Status</th>
                  <th>Questions</th>
                </tr>
              </thead>
              <tbody>
                @for (category of getCategories(); track category) {
                  @let areaData = auditAreas[category];
                  @let questions = getQuestionsByCategory(category);
                  <tr>
                    <td class="category-cell">{{ category }}</td>
                    <td>
                      <span class="score-value" [style.color]="getScoreColor(areaData.current)">
                        {{ areaData.current }}/10
                      </span>
                    </td>
                    <td>
                      <span class="score-value" [style.color]="getScoreColor(areaData.potential)">
                        {{ areaData.potential }}/10
                      </span>
                    </td>
                    <td>
                      @if (isPass(areaData.current)) {
                        <span class="status-badge pass">
                          <mat-icon>check_circle</mat-icon>
                          Pass
                        </span>
                      } @else {
                        <span class="status-badge fail">
                          <mat-icon>cancel</mat-icon>
                          Fail
                        </span>
                      }
                    </td>
                    <td class="questions-cell">
                      {{ getPassedCount(questions) }}/{{ getTotalCount(questions) }} passed
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    }
  
    <!-- Failed Questions Table -->
    @if (getFailedQuestions().length > 0) {
      <div class="results-section">
        <div class="performance-table">
          <div class="table-header">
            <div class="table-title-section">
              <h3>Issues and Recommendations</h3>
              <p class="table-subtitle">
                Questions that did not meet the criteria
              </p>
            </div>
          </div>
          <div class="table-container">
            <table class="audit-table">
              <thead>
                <tr>
                  <th>Audit Area</th>
                  <th>Question</th>
                  <th>Evidence</th>
                </tr>
              </thead>
              <tbody>
                @for (item of getFailedQuestions(); track item.question) {
                  <tr>
                    <td>{{ item.category }}</td>
                    <td>{{ item.question }}</td>
                    <td class="evidence-cell">{{ item.evidence || 'No evidence provided' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    } @else if (getCategories().length > 0) {
      <div class="results-section">
        <div class="no-issues-message">
          <mat-icon>check_circle</mat-icon>
          <span>No issues found. All criteria are met!</span>
        </div>
      </div>
    }
    </div>
  }

  <!-- HIDDEN: Evidence Sources / Data Sources & Citations (do not remove)
  @if (getEvidenceSources().length > 0) {
  <app-citation-card [citations]="getEvidenceSources()" [title]="'Evidence Sources (Tavily)'" [displayStyle]="'grid'"
    [maxDisplay]="6">
  </app-citation-card>
  }
  -->
  } @else if (loading) {
    <div class="loading-container">
      <app-common-circle-loader message="Processing Landing Page audit... This may take a few moments."></app-common-circle-loader>
    </div>
  } @else {
    <div class="no-data-container">
      <div class="no-data-content">
        <mat-icon>web</mat-icon>
        <h3>No Landing Page Audit Data Available</h3>
        <p>Landing Page audit will be automatically triggered when a website URL is detected in the project description.</p>
        <p class="hint-text">Add a website URL to your project description to enable this audit.</p>
      </div>
    </div>
  }
  
  @if (auditError) {
    <div class="error-container">
      <mat-icon>error</mat-icon>
      <p class="error-message">{{ auditError }}</p>
    </div>
  }